import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Link as LinkIcon, RefreshCw, QrCode, Share2, Users, Menu, X, Phone, FileDown, GraduationCap, Search } from 'lucide-react';
import { Footer } from '@/components/Common/Footer';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = '/api';

const FALLBACK_PROFILE = {
  name: "Joe Phillips",
  title: "Project Management Educator",
  location: "Sarasota, Florida",
  photo_url: "/images/joe-profile.jpg",
  highlights: [
    { number: "1.2M", label: "Students Trained" },
    { number: "40+", label: "Books Published" },
    { number: "30", label: "Years Experience" }
  ],
  pullquote: "I've spent 30 years teaching people how to manage projects. Besume is what they've been missing.",
  slug: "joe"
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuredProfile, setFeaturedProfile] = useState(FALLBACK_PROFILE);
  const [browseEnabled, setBrowseEnabled] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const { user } = useAuth();
  const startSellingHref = user ? '/dashboard/sell' : '/signup';

  useEffect(() => {
    fetch(`${API}/browse/enabled`).then(r => r.json()).then(d => setBrowseEnabled(d.enabled)).catch(() => {});

    const fetchFeatured = async () => {
      try {
        const res = await fetch(`${API}/profile/featured`);
        if (res.ok) {
          const data = await res.json();
          if (data.profile) setFeaturedProfile(data.profile);
        }
      } catch {}
    };
    fetchFeatured();

    axios.get(`${API}/profile/discover-preview?limit=25`)
      .then(res => setProfiles(res.data.profiles || []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0a0a0a]" data-testid="landing-page">

      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#fafafa]/95 backdrop-blur-sm border-b border-[#e0e0e0]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-serif font-black tracking-tight" data-testid="logo">besume</Link>
          <div className="hidden md:flex items-center gap-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const q = e.target.elements.q.value.trim();
                if (q) {
                  window.location.href = `/search/products?q=${encodeURIComponent(q)}`;
                }
              }}
              className="flex items-center"
              data-testid="landing-search-form"
            >
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[#999] absolute left-2 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                <input
                  type="text"
                  name="q"
                  placeholder="Search products..."
                  className="text-xs border border-[#e0e0e0] rounded-none py-1.5 pl-7 pr-2 focus:outline-none focus:border-[#0a0a0a] bg-white w-40"
                  data-testid="landing-search-input"
                />
              </div>
            </form>
            <a href="#how" className="text-sm text-[#555] hover:text-[#0a0a0a]">How It Works</a>
            <a href="#features" className="text-sm text-[#555] hover:text-[#0a0a0a]">Features</a>
            {browseEnabled && <Link to="/browse" className="text-sm text-[#555] hover:text-[#0a0a0a]">Browse</Link>}
            <Link to="/pro" className="text-sm text-[#555] hover:text-[#0a0a0a]">Pro</Link>
            <Link to="/login"><Button variant="ghost" className="text-sm" data-testid="login-btn">Log In</Button></Link>
            <Link to="/signup"><Button className="bg-[#0a0a0a] text-white hover:bg-[#333] text-sm px-5 py-2" data-testid="nav-signup-btn">Create yours — free</Button></Link>
          </div>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-sm" data-testid="login-btn-mobile">Log In</Button></Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-[#e0e0e0] rounded-md" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#e0e0e0] bg-[#fafafa] px-6 py-4 space-y-4">
            <a href="#how" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-[#555] hover:text-[#0a0a0a] py-2">How It Works</a>
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-[#555] hover:text-[#0a0a0a] py-2">Features</a>
            {browseEnabled && <Link to="/browse" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-[#555] hover:text-[#0a0a0a] py-2">Browse</Link>}
            <Link to="/pro" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-[#555] hover:text-[#0a0a0a] py-2">Pro</Link>
            <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-[#0a0a0a] text-white hover:bg-[#333] text-sm px-5 py-2">Get Started</Button>
            </Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            {/* V5.25.16 — equation kicker. Wraps to a smaller size on
                very narrow screens (<360px) so it never breaks across
                two lines on phones — the user spec was explicit about
                preferring a smaller-but-single-line render. */}
            <p
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-[#777] mb-5 whitespace-nowrap"
              data-testid="hero-kicker"
            >
              Resume + Business = <span className="text-[#0a0a0a] font-semibold">Besume</span>
            </p>
            <h1
              className="text-3xl md:text-5xl font-serif font-black leading-[1.1] mb-6"
              data-testid="hero-headline"
            >
              Tell AI what you do.
              <span className="block mt-[0.5em]">
                Besume shows<br />who you are.
              </span>
            </h1>
            <p
              className="text-2xl md:text-3xl font-serif font-bold text-[#0a0a0a] mb-3 leading-tight"
              data-testid="hero-subhead"
            >
              Where expertise becomes income.
            </p>
            <p className="text-sm text-[#999] mb-8">besume.com/you · Free to start · No credit card</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/signup">
                <Button className="bg-[#0a0a0a] text-white hover:bg-[#333] text-sm px-8 py-6 h-auto" data-testid="cta-create-btn">
                  Create Your Besume — Free
                </Button>
              </Link>
              <Button
                variant="outline"
                className="border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white text-sm px-8 py-6 h-auto"
                data-testid="cta-how-btn"
                onClick={() => {
                  // V5.25.8 — scroll to the new video section that
                  // sits between the live-profile carousel and the
                  // 3-step "How It Works" explanation. The previous
                  // `<a href="#how">` jumped past the demo video,
                  // which was the point of adding the section.
                  const el = document.getElementById('see-how-it-works');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                See How It Works
              </Button>
            </div>
          </div>

          {/* Right — Featured Profile Card */}
          <div className="border border-[#e0e0e0] bg-white" data-testid="hero-profile-card">
            <div className="px-8 pt-4">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#bbb]">This is a Besume</span>
            </div>
            <div className="text-center py-10 px-8 border-b border-[#e0e0e0] group">
              {featuredProfile.photo_url ? (
                <img
                  src={featuredProfile.photo_url}
                  alt={featuredProfile.name}
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-4 grayscale group-hover:grayscale-0 transition-all duration-300"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#e0e0e0] mx-auto mb-4 flex items-center justify-center text-2xl font-serif font-bold text-[#999]">
                  {featuredProfile.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
              )}
              <h3 className="text-2xl font-serif font-bold">{featuredProfile.name}</h3>
              <p className="text-[#555] mt-1 text-sm">{featuredProfile.title}</p>
              <p className="text-xs text-[#999] mt-1">{featuredProfile.location}</p>
            </div>

            {featuredProfile.highlights && featuredProfile.highlights.length > 0 && (
              <div className="grid grid-cols-3 border-b border-[#e0e0e0]">
                {featuredProfile.highlights.slice(0, 3).map((h, idx) => (
                  <div key={idx} className={`py-5 text-center ${idx < 2 ? 'border-r border-[#e0e0e0]' : ''}`}>
                    <span className="text-xl font-serif font-black">{h.number}</span>
                    <br /><span className="text-[10px] uppercase tracking-wider text-[#999]">{h.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="px-8 pb-6">
              <a href={`/${featuredProfile.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-[#999] hover:text-[#0a0a0a] underline underline-offset-4">
                View full Besume →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scrollable Profiles Row */}
      {profiles.length > 0 && (
        <section className="py-16 border-t border-[#e0e0e0]">
          <div className="max-w-6xl mx-auto px-6 mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-2">Real people. Real profiles.</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">Built in minutes. Shared everywhere.</h2>
          </div>

          {/* Scrollable row with arrows */}
          <div className="max-w-6xl mx-auto px-6 relative">
            {/* Left arrow */}
            <button
              onClick={() => {
                document.getElementById('profiles-row').scrollBy({ left: -400, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-[#e0e0e0] rounded-full flex items-center justify-center hover:border-[#aaa] transition-colors shadow-sm"
              aria-label="Scroll left"
              data-testid="profiles-scroll-left"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>

            {/* Right arrow */}
            <button
              onClick={() => {
                document.getElementById('profiles-row').scrollBy({ left: 400, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-[#e0e0e0] rounded-full flex items-center justify-center hover:border-[#aaa] transition-colors shadow-sm"
              aria-label="Scroll right"
              data-testid="profiles-scroll-right"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>

            {/* Scrollable strip */}
            <div
              id="profiles-row"
              className="flex gap-3 overflow-x-auto px-8 pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {profiles.map((p) => (
                <a
                  key={p.slug}
                  href={`/${p.slug}`}
                  className="flex-none w-40 bg-white border border-[#e0e0e0] rounded-xl p-4 hover:border-[#aaa] transition-colors no-underline"
                  data-testid={`homepage-profile-${p.slug}`}
                >
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.display_name} className="w-10 h-10 rounded-full object-cover mb-3 grayscale hover:grayscale-0 transition-all" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#f0f0f0] mb-3 flex items-center justify-center text-sm font-medium text-[#999]">
                      {p.display_name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div className="text-sm font-medium text-[#0a0a0a] truncate">{p.display_name}</div>
                  <div className="text-xs text-[#777] mt-0.5 truncate">{p.title}</div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* V5.25.8 — Demo video section. Lives between the live-profile
          carousel ("Built in minutes. Shared everywhere.") and the
          3-step "How It Works" explanation. The hero "See How It
          Works" button targets `#see-how-it-works`. Video is muted +
          controls + preload="metadata" — no autoplay with sound, so
          we never violate browser autoplay policies and the user
          chooses when to watch. */}
      <section
        id="see-how-it-works"
        className="py-24 px-6 border-t border-[#e0e0e0] bg-[#fafaf7] scroll-mt-20"
        data-testid="landing-demo-video-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-4">See it in action</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3">
            See how Besume works
          </h2>
          <p className="text-sm text-[#777] mb-10">40 seconds</p>
          <div className="rounded-lg overflow-hidden shadow-2xl bg-black">
            <video
              src="/quickbuild-demo.mp4"
              poster="/quickbuild-demo-poster.jpg"
              controls
              muted
              playsInline
              preload="metadata"
              className="w-full h-auto block"
              data-testid="landing-demo-video"
            />
          </div>
        </div>
      </section>

      {/* V5.25.16 — Pull quote. Lives between the demo video and the
          "How it works" three-step. No header, no subtitle, no CTA,
          no decoration — the silence around the line is the design.
          py-24 (≈6rem) top and bottom per the spec; centered, white
          background to match the page rhythm. */}
      <section
        className="py-24 px-6 bg-white"
        data-testid="landing-pull-quote-section"
        aria-label="A resume is a document. A Besume is a person."
      >
        <p
          className="text-center font-serif font-bold leading-[1.15] text-[#0a0a0a] text-4xl md:text-5xl max-w-4xl mx-auto"
          data-testid="landing-pull-quote"
        >
          A resume is a document.<br />
          A Besume is a person.
        </p>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 border-t border-[#e0e0e0]" id="how">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-4">How It Works</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-16">
            As fast as five minutes.<br />One conversation.<br />One link.
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { n: '01', title: 'A page that sells your expertise', desc: 'Our AI asks the right questions — the ones that pull out your best stories, accomplishments, and what makes you different. No forms. No templates. Just talk.' },
              { n: '02', title: 'Endorsements that prove it', desc: 'Request recommendations from clients and colleagues. Social proof that speaks louder than any bullet point.' },
              { n: '03', title: 'One link that does the work', desc: 'besume.com/you — drop it in your LinkedIn bio, email signature, or business card. One link tells your story, sells your expertise, and grows your audience.' },
            ].map((step) => (
              <div key={step.n}>
                <div className="text-6xl font-serif font-black text-[#e0e0e0] mb-4">{step.n}</div>
                <h3 className="text-xl font-serif font-semibold mb-3">{step.title}</h3>
                <p className="text-[#555] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* V5.25.14 — "When to send it" use-case strip. Sits between
          the three-step section and the feature grid. Reuses the
          same card pattern (`border border-[#e0e0e0] p-6`) as the
          "Everything you need" feature grid below so it reads as
          part of the same visual system, not bolted on. */}
      <section className="py-24 px-6 border-t border-[#e0e0e0]">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-4">When to send it</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-16">
            Send your Besume when…
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Someone asks what you do.', desc: 'Your Besume is the answer — clearer than a resume, more personal than LinkedIn, and built to be shared in one tap.' },
              { title: 'A client wants to hire you.', desc: 'Bookable calls, consulting packages, services — clients buy and book directly from your page. No separate scheduling tool needed.' },
              { title: 'You want to sell your expertise.', desc: 'Courses, downloads, guides, templates, coaching, and anything digital — list them on your Besume and your profile becomes your storefront.' },
            ].map((useCase, idx) => (
              <div key={idx} className="border border-[#e0e0e0] p-6" data-testid={`landing-usecase-card-${idx}`}>
                <h3 className="text-lg font-serif font-semibold mb-3">{useCase.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 bg-white border-t border-[#e0e0e0]" id="features">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-4">
            Everything you need.<br />In one place.
          </h2>
          <p className="text-[#555] mb-16 max-w-2xl">No credit card required. No trial. Your Besume, your storefront, your audience.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Endorsements', desc: 'Request recommendations from clients and colleagues. They appear on your page as social proof that speaks louder than any bullet point.' },
              { icon: GraduationCap, title: 'Sell Your Work', desc: 'Digital downloads, consulting calls, courses. List anything and sell it directly from your Besume. Visitors buy instantly — no separate storefront needed.' },
              { icon: LinkIcon, title: 'Your Own URL', desc: 'besume.com/yourname — clean, memorable, permanently yours. AI builds the page behind it.' },
              { icon: RefreshCw, title: 'Weekly Updates', desc: 'We email you each week: "What are you working on?" Reply, and your page updates automatically. Stay current without logging in.' },
              { icon: QrCode, title: 'QR Code', desc: 'Auto-generated for your Besume. Print it on your business cards, your resume, or your socials.' },
              { icon: Share2, title: 'LinkedIn Ready', desc: 'One-click copy a perfectly formatted snippet to share your Besume on LinkedIn. Optimized to get clicks.' },
            ].map((feature, idx) => (
              <div key={idx} className="border border-[#e0e0e0] p-6">
                <feature.icon className="w-6 h-6 mb-4 text-[#333]" strokeWidth={1.5} />
                <h3 className="text-lg font-serif font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storefront */}
      <section className="py-24 px-6 bg-[#f9f9f9] border-t border-[#e0e0e0]" id="commerce">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-4">Sell From Your Besume</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-4">
            Your Besume.<br />Your storefront.
          </h2>
          <p className="text-[#555] mb-16 max-w-2xl">Everything you sell lives on your Besume. No separate storefront. No extra tools. One link does everything.</p>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { icon: FileDown, title: 'Digital Downloads', desc: 'Sell templates, guides, ebooks, or any digital file. Visitors purchase and download instantly — no shipping, no inventory.' },
              { icon: Phone, title: 'Bookable Calls', desc: 'Offer paid consultations, coaching sessions, or discovery calls directly from your Besume. Visitors book and pay in one place.' },
              { icon: GraduationCap, title: 'Online Courses', desc: 'Link your course on any platform — Teachable, Thinkific, Kajabi, or anywhere you host. Your Besume becomes the front door to everything you teach.' },
            ].map((item, idx) => (
              <div key={idx} className="border border-[#e0e0e0] bg-white p-6">
                <item.icon className="w-6 h-6 mb-4 text-[#333]" strokeWidth={1.5} />
                <h3 className="text-lg font-serif font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to={startSellingHref}>
              <Button variant="outline" className="border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white text-sm px-8 py-6 h-auto" data-testid="start-selling-btn">
                Start Selling
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 px-6 bg-white border-t border-[#e0e0e0]" data-testid="comparison-table-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#999] mb-4">Why not just use LinkedIn?</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-16">
            Less resume.<br />More Besume.
          </h2>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[640px] text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b-2 border-[#0a0a0a]">
                  <th className="text-left py-3 pr-4 font-normal text-[#999]">Feature</th>
                  <th className="py-3 px-4 text-center font-serif font-bold text-[#0a0a0a] bg-[#f5f5f5]">Besume</th>
                  <th className="py-3 px-4 text-center font-normal text-[#555]">LinkedIn</th>
                  <th className="py-3 px-4 text-center font-normal text-[#555]">Kajabi</th>
                  <th className="py-3 px-4 text-center font-normal text-[#555]">Gumroad</th>
                  <th className="py-3 px-4 text-center font-normal text-[#555]">Linktree</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['AI-built profile', true, false, false, false, false],
                  ['Sell digital products', true, false, true, true, false],
                  ['Social proof / endorsements', true, true, false, false, false],
                  ['Single shareable link', true, true, false, false, true],
                  ['Free to start', true, true, false, true, true],
                  ['No monthly subscription', true, true, false, false, true],
                  ['Buyer needs no account', true, false, false, true, false],
                  ['Built-in messaging', true, true, true, false, false],
                  ['QR code included', true, false, false, false, false],
                  ['AI content assistant', true, false, false, false, false],
                  ['Custom domain included', 'Pro', false, 'extra', false, 'extra'],
                ].map(([feature, ...vals], idx) => (
                  <tr key={idx} className="border-b border-[#e0e0e0]">
                    <td className="py-3 pr-4 text-[#333]">{feature}</td>
                    {vals.map((v, i) => (
                      <td key={i} className={`py-3 px-4 text-center ${i === 0 ? 'bg-[#f5f5f5]' : ''}`}>
                        {v === true ? <span className="font-bold text-[#0a0a0a]">&#10003;</span>
                          : v === false ? <span className="text-[#ccc]">&mdash;</span>
                          : <span className="text-xs text-[#999]">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Founder block — quiet pause between comparison and pricing */}
      <section className="py-20 px-6 bg-white border-t border-[#e0e0e0]" data-testid="founder-block">
        <div className="max-w-[640px] mx-auto text-center">
          <img
            src="/images/joe-profile.jpg"
            alt="Joe Phillips"
            width={80}
            height={80}
            className="w-20 h-20 rounded-full object-cover grayscale hover:grayscale-0 transition-all duration-300 mx-auto mb-8 border border-[#e0e0e0]"
            data-testid="founder-photo"
          />
          <p
            className="font-serif italic text-[#333]"
            style={{ fontSize: '19px', lineHeight: 1.6 }}
            data-testid="founder-quote"
          >
            I know what it&rsquo;s like to be reduced to a job title.<br />
            I know what it&rsquo;s like to be seen as a one-sheet resume.<br />
            I know what it&rsquo;s like to build something and watch the platform<br />
            take more than they earned.
          </p>
          <p
            className="font-serif text-[#222] mt-6"
            style={{ fontSize: '19px', lineHeight: 1.6 }}
          >
            So I built Besume.
          </p>
          <p className="text-sm text-[#999] mt-8" data-testid="founder-signature">
            &mdash; Joe Phillips, Founder
          </p>
        </div>
      </section>

      {/* Pro */}
      <section className="py-24 px-6 bg-[#0a0a0a] text-white" id="pro">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#777] mb-4">Besume Pro</p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-6">
            Serious about your expertise.<br />Serious about your income.
          </h2>
          <div className="text-6xl font-serif font-black mb-2">$99</div>
          <p className="text-[#777] mb-1">per year</p>
          <p className="text-xs text-[#555] mb-12" data-testid="pro-per-month-equiv">Less than $9 a month</p>
          <div className="grid md:grid-cols-3 gap-8 text-left mb-12">
            {[
              { title: 'Sell Unlimited', desc: 'List as many products as you want. Pro sellers keep more of every sale with our lowest platform fee of just 5%.' },
              { title: 'Services Section', desc: 'Showcase what you offer with pricing. Turn your Besume into a sales page. List consulting packages, retainers, or ongoing work.' },
              { title: 'Custom Domain', desc: 'Connect your own domain — yourname.com — directly to your Besume. Free SSL included.' },
              { title: 'Priority in Browse', desc: 'Pro profiles appear at the top of search results and category listings. Get seen first, get contacted more.' },
              { title: 'Analytics Dashboard', desc: "See exactly who's visiting, where they came from, what device they're on, and how your traffic trends over time." },
              { title: 'Coupon Codes', desc: 'Create discount codes for your products and services. Run promotions and reward loyal clients on your own terms.' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-[#2a2a2a] border border-[#444] p-6">
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-[#ccc]">{feature.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/pro">
            <Button className="bg-white text-[#0a0a0a] hover:bg-[#f0f0f0] px-8 py-6 h-auto text-sm">
              Learn More About Pro
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 text-center">
        <h2 className="text-5xl md:text-6xl font-serif font-black mb-6">Start earning.</h2>
        <p className="text-[#555] mb-10">Your profile. Your storefront. Five minutes.</p>
        <Link to="/signup">
          <Button className="bg-[#0a0a0a] text-white hover:bg-[#333] px-10 py-6 h-auto text-sm mb-4" data-testid="cta-final-btn">
            Create Your Besume
          </Button>
        </Link>
        <p className="text-sm text-[#999]">No credit card. No trial. Just your story, told well.</p>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default LandingPage;
